import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * ForeverChrist storefront components.
 *
 * <fc-product-card>   Swatches swap the photo; size buttons add straight to the cart through
 *                     Horizon's cart events, so the cart drawer and counts update as usual.
 * <fc-cart-count>     Keeps the header "BAG (n)" count in sync.
 * <fc-menu-drawer>    Full-screen mobile menu.
 * <fc-collection>     Filter panel toggle, auto-submitting filters/sort, grid density.
 */

const root = window.Shopify?.routes?.root || '/';

/**
 * Adds a variant using the AJAX cart, announcing it with Horizon's standard cart event.
 * @param {HTMLElement} source
 * @param {number|string} variantId
 */
async function addToCart(source, variantId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  let didError = false;
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!response.ok) didError = true;
  } catch {
    didError = true;
  }

  try {
    const cart = await (await fetch(`${root}cart.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: 1, didError, source: 'fc-product-card' },
    });
  } catch (error) {
    deferred.reject(error);
  }

  return !didError;
}

class FcProductCard extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-fc-variants]');
    /** @type {{id:number, available:boolean, options:string[]}[]} */
    this.variants = data ? JSON.parse(data.textContent || '[]') : [];
    this.colorIndex = Number(this.dataset.colorIndex ?? -1);
    this.sizeIndex = Number(this.dataset.sizeIndex ?? -1);
    this.color = this.dataset.selectedColor || '';
    this.addEventListener('click', this.#onClick);
    this.#updateSizes();
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const swatch = target.closest('[data-fc-color]');
    if (swatch instanceof HTMLElement) {
      event.preventDefault();
      this.#selectColor(swatch);
      return;
    }

    if (target.closest('[data-fc-quick-open]')) {
      event.preventDefault();
      this.querySelector('[data-fc-quick-panel]')?.removeAttribute('hidden');
      this.querySelector('[data-fc-quick-open]')?.setAttribute('hidden', '');
      return;
    }

    if (target.closest('[data-fc-quick-close]')) {
      event.preventDefault();
      this.#closeQuick();
      return;
    }

    const sizeButton = target.closest('[data-fc-size]');
    if (sizeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      if (sizeButton.hasAttribute('data-unavailable')) return;
      const variant = this.#findVariant(sizeButton.dataset.fcSize || '');
      if (!variant) return;
      sizeButton.setAttribute('aria-busy', 'true');
      await addToCart(this, variant.id);
      sizeButton.removeAttribute('aria-busy');
      this.#closeQuick();
    }
  };

  #closeQuick() {
    this.querySelector('[data-fc-quick-panel]')?.setAttribute('hidden', '');
    this.querySelector('[data-fc-quick-open]')?.removeAttribute('hidden');
  }

  /** @param {HTMLElement} swatch */
  #selectColor(swatch) {
    this.color = swatch.dataset.fcColor || '';
    for (const button of this.querySelectorAll('[data-fc-color]')) {
      button.setAttribute('aria-pressed', String(button === swatch));
    }
    const name = this.querySelector('[data-fc-color-name]');
    if (name) name.textContent = this.color;

    const img = this.querySelector('.fc-card__img--primary');
    if (img instanceof HTMLImageElement && swatch.dataset.imageSrc) {
      img.srcset = swatch.dataset.imageSrcset || '';
      img.src = swatch.dataset.imageSrc;
    }
    this.#updateSizes();
  }

  /** @param {string} size */
  #findVariant(size) {
    return this.variants.find((variant) => {
      const colorOk = this.colorIndex < 0 || !this.color || variant.options[this.colorIndex] === this.color;
      const sizeOk = this.sizeIndex < 0 || !size || variant.options[this.sizeIndex] === size;
      return colorOk && sizeOk && variant.available;
    });
  }

  #updateSizes() {
    for (const button of this.querySelectorAll('[data-fc-size]')) {
      if (!(button instanceof HTMLElement)) continue;
      const available = Boolean(this.#findVariant(button.dataset.fcSize || ''));
      button.toggleAttribute('data-unavailable', !available);
    }
  }
}

class FcCartCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onUpdate);
  }

  /** @param {any} event */
  #onUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (typeof count === 'number') {
          this.textContent = String(count);
          this.closest('[data-fc-cart]')?.toggleAttribute('data-empty', count === 0);
        }
      })
      .catch(() => {});
  };
}

class FcMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    document.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-fc-menu-open]')) {
      this.dialog?.showModal();
      document.documentElement.classList.add('fc-scroll-lock');
    } else if (target.closest('[data-fc-menu-close]')) {
      this.dialog?.close();
      document.documentElement.classList.remove('fc-scroll-lock');
    }
  };
}

class FcCollection extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-fc-filters]');
    this.form?.addEventListener('change', () => this.#submit());
    this.querySelector('[data-fc-sort]')?.addEventListener('change', () => this.#submit());
    this.addEventListener('click', this.#onClick);

    try {
      const density = localStorage.getItem('fc-grid-density');
      if (density) this.#setDensity(density);
    } catch {}
  }

  #submit() {
    if (!this.form) return;
    const params = new URLSearchParams(/** @type {any} */ (new FormData(this.form)));
    const sort = this.querySelector('[data-fc-sort]');
    if (sort instanceof HTMLSelectElement && sort.value) params.set('sort_by', sort.value);
    for (const [key, value] of [...params.entries()]) {
      if (value === '') params.delete(key);
    }
    window.location.search = params.toString();
  }

  /** @param {string} value */
  #setDensity(value) {
    this.style.setProperty('--fc-grid-cols', value);
    for (const button of this.querySelectorAll('[data-fc-density]')) {
      button.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (button).dataset.fcDensity === value));
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const density = target.closest('[data-fc-density]');
    if (density instanceof HTMLElement) {
      const value = density.dataset.fcDensity || '3';
      this.#setDensity(value);
      try {
        localStorage.setItem('fc-grid-density', value);
      } catch {}
      return;
    }

    if (target.closest('[data-fc-filter-toggle]')) {
      if (window.matchMedia('(min-width: 990px)').matches) {
        // Desktop: show/hide the sidebar.
        const open = !this.hasAttribute('data-filters-open');
        this.toggleAttribute('data-filters-open', open);
        const label = this.querySelector('[data-fc-filter-label]');
        if (label instanceof HTMLElement) label.textContent = open ? label.dataset.hide || '' : label.dataset.show || '';
      } else {
        // Mobile: open the bottom sheet.
        this.setAttribute('data-sheet-open', '');
        document.documentElement.classList.add('fc-scroll-lock');
      }
      return;
    }

    if (target.closest('[data-fc-filter-close]')) {
      this.removeAttribute('data-sheet-open');
      document.documentElement.classList.remove('fc-scroll-lock');
    }
  };
}

if (!customElements.get('fc-product-card')) customElements.define('fc-product-card', FcProductCard);
if (!customElements.get('fc-cart-count')) customElements.define('fc-cart-count', FcCartCount);
if (!customElements.get('fc-menu-drawer')) customElements.define('fc-menu-drawer', FcMenuDrawer);
if (!customElements.get('fc-collection')) customElements.define('fc-collection', FcCollection);
